import { DeleteOutlined, DeleteOutlineOutlined } from "@mui/icons-material";
import {
  Button,
  Input,
  InputNumber,
  message,
  Space,
  Table,
  Typography,
} from "antd";
import COLORS from "common/Colors";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getAllDepartmentList } from "redux/selectors";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import styled from "styled-components";
import GridBuilder from "UIBuilderEngine/GridBuilder";

const { Text, Title } = Typography;

const ScoreAndIncrementWrap = styled.div`
  .footer-content {
    cursor: pointer;
    width: 100%;
    text-align: center;
    color: #2d53da;
    background: #e3e8fc;
    border-radius: 5px;
    padding: 8px 0;
    margin-bottom: 20px;
  }
  .no-data {
    width: 100%;
    text-align: center;
    border-radius: 5px;
    background: #f5f5f5;
    padding: 40px 10px;
  }
`;

const StyledTable = styled(Table)`
  .ant-table {
    border: 1px solid #ccc;
    border-radius: 12px;
    overflow: hidden;
  }

  .ant-table-container {
    border-radius: 12px;
  }

  .ant-table-content {
    overflow-x: auto;
  }

  table {
    width: 100%;
    min-width: 900px;
  }

  .ant-table-cell {
    padding: 16px;
    white-space: nowrap;
  }

  .ant-table-thead .ant-table-cell {
    font-weight: 600;
    color: ${COLORS.SECONDARY_BLACK};
  }
  .ant-table-tbody > tr > td {
    border-bottom: none !important;
  }

  .reason-column {
    min-width: 280px;
    white-space: normal;
  }

  .non-white {
    background: #f9f9f9;
  }

  @media (max-width: 768px) {
    .ant-table-cell {
      padding: 10px;
      font-size: 12px;
    }
  }
`;

const scoreRanges = [
  { key: "score1", label: "Score 1", from: 0 },
  { key: "score2", label: "Score 2", from: 6 },
  { key: "score3", label: "Score 3", from: 9 },
  { key: "score4", label: "Score 4", from: 11 },
  { key: "score5", label: "Score 5", from: 14 },
];

const ScoreAndIncrement = ({
  setStep,
  step,
  data,
  setData,
  tableData,
  setTableData,
  setIsAddEditTemplate,
  isAddEditTemplate,
  status,
  setStatus,
  onSubmitClick,
  loading,
  draftLoading,
  scrollToTop,
  validateAll,
}) => {
  console.log("tabledata2--->", data);
  console.log("tabledata-->", tableData);

  const [weightageError, setWeightageError] = useState("");
  const [invalidFields, setInvalidFields] = useState({
    strategic: [],
    previous: [],
    upcoming: [],
    bdo: [],
  });
  const [invalidSalaryFields, setInvalidSalaryFields] = useState([]);

  const scoreConfig = useMemo(() => {
    return tableData.map((_, index) => ({
      key: `score${index + 1}`,
      label: `Score ${index + 1}`,
      from: 0,
    }));
  }, [tableData.length]);

  const handleTableDataChange = (index, field, value) => {
    let updated = tableData.map((item, idx) =>
      idx === index ? { ...item, [field]: value } : item
    );

    // ✅ If salaryto changed and a next row exists, update its salaryfrom
    if (field === "salaryto" && index < updated.length - 1) {
      updated = updated.map((item, idx) =>
        idx === index + 1
          ? { ...item, salaryfrom: value !== "" ? Number(value) + 1 : 0 }
          : item
      );
    }

    setTableData(updated);

    if (field === "weightage") {
      const total = updated.reduce(
        (sum, item) => sum + (parseFloat(item.weightage) || 0),
        0
      );
      setWeightageError(
        total !== 100 ? "Total weightage must be exactly 100%" : ""
      );
    }

    setInvalidFields((prev) => {
      const updatedInvalid = { ...prev };
      if (updatedInvalid.strategic.includes(index)) {
        if (value) {
          updatedInvalid.strategic = updatedInvalid.strategic.filter(
            (i) => i !== index
          );
        }
      }
      return updatedInvalid;
    });

    const currentRow = updated[index];
    const isLastRow = index === updated.length - 1;
    const isCurrentRowComplete =
      currentRow.salaryto !== "" &&
      currentRow.salaryto !== null &&
      currentRow.label !== "" &&
      currentRow.label !== null &&
      currentRow.description !== "" &&
      currentRow.description !== null;

    const isValidSalaryRange =
      field !== "salaryto" ||
      value === "" ||
      Number(value) > Number(currentRow.salaryfrom);

    if (
      isLastRow &&
      isCurrentRowComplete &&
      Number(currentRow.salaryto) !== 100 &&
      isValidSalaryRange
    ) {
      const newRow = {
        id: updated.length + 1,
        salaryfrom: Number(currentRow.salaryto) + 1,
        salaryto: "",
        label: "",
        description: "",
      };
      setTableData([...updated, newRow]);
    }
  };

  const columns = [
    {
      title: "Score",
      dataIndex: "id",
      key: "id",
      width: "10%",
    },
    {
      title: <div style={{ whiteSpace: "wrap" }}>Score Range From (%)</div>,
      dataIndex: "salaryfrom",
      key: "salaryfrom",
      width: "20%",
    },
    {
      title: "Score Range To (%)",
      dataIndex: "salaryto",
      key: "salaryto",
      width: "20%",
      render: (text, record, index) => {
        const isLastRow = tableData[tableData.length - 1]?.id === record.id; // ✅
        const isSalaryRangeInvalid =
          record.salaryto !== "" &&
          record.salaryto !== null &&
          Number(record.salaryto) <= Number(record.salaryfrom);

        return (
          <>
            {status === "view" ? ( // ✅ disable if not last row
              record?.salaryto
            ) : (
              <>
                <Input.TextArea
                  autoSize={{ minRows: 1, maxRows: 6 }}
                  value={record?.salaryto}
                  style={{
                    width: "100%",
                    color: record?.salaryto !== 100 ? "" : "green",
                    border:
                      (invalidFields.strategic.includes(index) &&
                        !record.salaryto) ||
                      isSalaryRangeInvalid
                        ? "1px solid red"
                        : "",
                  }}
                  onKeyDown={(event) => {
                    const allowedKeys = [
                      "Backspace",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "Delete",
                    ];
                    if (
                      !/[0-9]/.test(event.key) &&
                      !allowedKeys.includes(event.key)
                    ) {
                      event.preventDefault();
                      return;
                    }
                    const currentValue = event.target.value;
                    const newValue = parseInt(currentValue + event.key, 10);
                    if (!isNaN(newValue) && newValue > 100) {
                      event.preventDefault();
                    }
                  }}
                  status={
                    (invalidFields.strategic.includes(index) &&
                      !record.salaryto) ||
                    isSalaryRangeInvalid
                      ? "error"
                      : ""
                  }
                  onChange={(e) => {
                    const newValue = Number(e.target.value);
                    // if (
                    //   e.target.value !== "" &&
                    //   newValue <= Number(record.salaryfrom)
                    // ) {
                    //   message.error(
                    //     `Score Range To must be greater than Score Range From (${record.salaryfrom})`
                    //   );
                    // }
                    handleTableDataChange(index, "salaryto", newValue);
                  }}
                />
                {isSalaryRangeInvalid && (
                  <div style={{ color: "red", fontSize: "12px", marginTop: 4 }}>
                    Must be greater than Score Range From ({record.salaryfrom})
                  </div>
                )}
              </>
            )}
          </>
        );
      },
    },
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
      width: "20%",
      render: (text, record, index) => {
        const isLastRow = tableData[tableData.length - 1]?.id === record.id; // ✅
        return (
          <>
            {status === "view" ? ( // ✅ disable if not last row
              <div className="text-wrap">{record?.label}</div>
            ) : (
              <Input.TextArea
                autoSize={{ minRows: 1, maxRows: 6 }}
                value={record?.label}
                style={{
                  width: "100%",
                  border:
                    invalidFields.strategic.includes(index) && !record.label
                      ? "1px solid red"
                      : "",
                }}
                status={
                  invalidFields.strategic.includes(index) && !record.label
                    ? "error"
                    : ""
                }
                onChange={(e) =>
                  handleTableDataChange(index, "label", e.target.value)
                }
              />
            )}
          </>
        );
      },
    },
    {
      title: "Performance Description",
      dataIndex: "description",
      key: "description",
      width: "30%",
      render: (text, record, index) => {
        const isLastRow = tableData[tableData.length - 1]?.id === record.id; // ✅
        return (
          <>
            {status === "view" ? ( // ✅ disable if not last row
              <div className="text-wrap">{record?.description}</div>
            ) : (
              <Input.TextArea
                autoSize={{ minRows: 1, maxRows: 6 }}
                value={record?.description}
                style={{
                  border:
                    invalidFields.strategic.includes(index) &&
                    !record.description
                      ? "1px solid red"
                      : "",
                }}
                status={
                  invalidFields.strategic.includes(index) && !record.description
                    ? "error"
                    : ""
                }
                onChange={(e) =>
                  handleTableDataChange(index, "description", e.target.value)
                }
              />
            )}
          </>
        );
      },
    },
    status !== "view" && {
      title: "",
      key: "action",
      render: (text, record, index) => {
        const isLastRow = tableData[tableData.length - 1]?.id === record.id;
        return (
          isLastRow &&
          index !== 0 && (
            <DeleteOutlineOutlined
              disabled={tableData?.length === 1}
              onClick={() => handleDeleteRow(record.id)}
              style={{ color: "#2D53DA", cursor: "pointer" }}
            />
          )
        );
      },
    },
  ].filter(Boolean);

  const handleAddRow = () => {
    const lastRow = tableData[tableData.length - 1];

    if (!lastRow.salaryto || !lastRow.label || !lastRow.description) {
      message.error(
        "Please complete all fields in the current row before adding another"
      );
      setInvalidFields((prev) => ({
        ...prev,
        strategic: [...prev.strategic, tableData.length - 1],
      }));
      return;
    }

    // Validate salaryto > salaryfrom before adding
    if (Number(lastRow.salaryto) <= Number(lastRow.salaryfrom)) {
      // message.error(
      //   `Score Range To must be greater than Score Range From (${lastRow.salaryfrom})`
      // );
      return;
    }

    const newRow = {
      id: tableData.length + 1,
      salaryfrom: Number(lastRow.salaryto) + 1,
      salaryto: "",
      label: "",
      description: "",
    };

    setTableData([...tableData, newRow]);
  };

  const handleDeleteRow = (id) => {
    const filteredData = tableData.filter((item) => item.id !== id);
    const updatedData = filteredData.map((item, index) => ({
      ...item,
      id: index + 1,
    }));
    setTableData(updatedData);
  };

  const handleSalaryDataChange = (key, field, value) => {
    const updatedData = data.map((row) =>
      row.key === key ? { ...row, [field]: value } : row
    );
    setData(updatedData);

    setInvalidSalaryFields((prev) =>
      prev.filter((item) => {
        if (item.key === key) {
          const updatedFields = item.fields.filter((f) => f !== field);
          return updatedFields.length > 0;
        }
        return true;
      })
    );
  };

  // Helper: check if a score column should be skipped because a previous
  // score already reached 100 (making further columns irrelevant)
  const isScoreSkipped = (rowKey, scoreIndex) => {
    if (scoreIndex === 0) return false;
    // Walk back through previous scores — if any of them hit 100, skip this one
    for (let i = 0; i < scoreIndex; i++) {
      const prevKey = scoreConfig[i].key;
      const row = data.find((r) => r.key === rowKey);
      if (Number(row?.[prevKey]) >= 100) return true;
    }
    return false;
  };

  const addRow = () => {
    const lastRow = data[data.length - 1];

    const invalidFieldsList = [];
    if (!lastRow?.experience) invalidFieldsList.push("experience");

    scoreConfig.forEach((score, scoreIndex) => {
      // Skip scores that come after one that already reached 100
      if (isScoreSkipped(lastRow.key, scoreIndex)) return;
      const val = lastRow?.[score.key];
      if (val === "" || val === null || val === undefined) {
        invalidFieldsList.push(score.key);
      }
    });

    if (invalidFieldsList.length > 0) {
      message.error(
        "Please complete all required fields in the current row before adding another"
      );
      setInvalidSalaryFields([
        {
          index: data.length - 1,
          key: lastRow.key,
          fields: invalidFieldsList,
        },
      ]);
      return;
    }

    setData([
      ...data,
      {
        key: data.length,
        experience: "",
        score1: "",
        score2: "",
        score3: "",
        score4: "",
        score5: "",
      },
    ]);
  };

  const deleteRow = (key) => {
    setData(data.filter((row) => row.key !== key));
  };

  // Returns the "from" value for a given score column in a row.
  // For score1: it's the previous row's score5 (or 0 for the first row).
  // For score2-5: it's the current row's previous score value.
  // Returns null if the previous score hasn't been filled yet.
  const getPreviousScoreTo = (rowKey, scoreIndex) => {
    const currentRow = data.find((row) => row.key === rowKey);

    if (scoreIndex === 0) {
      return 0; // ✅ Score 1 always starts from 0 for every row
    }

    const previousScoreKey = scoreConfig[scoreIndex - 1].key;
    const value = currentRow?.[previousScoreKey];

    if (value === undefined || value === "" || value === null) return null;
    return Number(value);
  };

  // A score column input is disabled when:
  // 1. Previous score hasn't been filled (null), OR
  // 2. Previous score's value has already reached 100 (no room left)
  const isScoreDisabled = (rowKey, scoreIndex) => {
    if (scoreIndex === 0) return false;
    const previousTo = getPreviousScoreTo(rowKey, scoreIndex);
    if (previousTo === null) return true; // previous not yet filled
    if (Number(previousTo) >= 100) return true; // previous already at 100
    return false;
  };

  const validateScoreValue = (rowKey, scoreKey, value, fromValue) => {
    if (value === "" || value === null || value === undefined) {
      return true;
    }

    const numValue = parseFloat(value);
    const fromNum = parseFloat(fromValue);

    console.log("numValue:", numValue, "fromNum:", fromNum);

    if (isNaN(numValue)) {
      message.error("Please enter a valid number");
      return false;
    }

    if (!isNaN(fromNum) && numValue < fromNum) {
      // message.error(`Value must be greater than ${fromNum}`);
      return false;
    }

    if (numValue > 100) {
      message.error("Maximum value is 100%");
      return false;
    }

    return true;
  };

  const salaryColumns = [
    {
      title: "Experience (Yrs)",
      dataIndex: "experience",
      render: (_, record) => {
        const hasError = invalidSalaryFields.some(
          (item) =>
            item.key === record.key && item.fields.includes("experience")
        );

        return (
          <>
            {status === "view" ? (
              record.experience
            ) : (
              <Input
                value={record.experience}
                style={{
                  width: "100%",
                  marginTop: "0px",
                  border: hasError ? "1px solid red" : "",
                }}
                onKeyDown={(event) => {
                  if (
                    !/[0-9]/.test(event.key) &&
                    event.key !== "Backspace" &&
                    event.key !== "Tab" &&
                    event.key !== "-"
                  ) {
                    event.preventDefault();
                  }
                }}
                maxLength={3}
                status={hasError ? "error" : ""}
                onChange={(e) =>
                  handleSalaryDataChange(
                    record.key,
                    "experience",
                    e.target.value
                  )
                }
              />
            )}
          </>
        );
      },
    },

    ...scoreConfig.map((score, scoreIndex) => ({
      title: score.label,
      render: (_, record) => {
        const fromValue = getPreviousScoreTo(record.key, scoreIndex);
        // fromValue is null => previous not filled; use 0 for display
        const displayFrom = fromValue !== null ? fromValue + 1 : 0;

        const disabled = isScoreDisabled(record.key, scoreIndex);

        // Check if THIS score's value is invalid (to <= from)
        const currentVal = record[score.key];
        const minAllowed = fromValue !== null ? Number(fromValue) + 1 : 0;
        const isRangeInvalid =
          !disabled &&
          currentVal !== "" &&
          currentVal !== null &&
          currentVal !== undefined &&
          fromValue !== null &&
          Number(currentVal) <= minAllowed;

        const hasError =
          invalidSalaryFields.some(
            (item) => item.key === record.key && item.fields.includes(score.key)
          ) || isRangeInvalid;

        console.log(
          "record123-->",
          record.key,
          scoreIndex,
          fromValue,
          record[score.key]
        );

        return (
          <>
            {status === "view" ? (
              <>
                {displayFrom}% to {record[score.key]} %
              </>
            ) : (
              <Space
                key={`score-${record.key}-${scoreIndex}`}
                style={{ width: "100%" }}
                direction="vertical"
                size={0}
              >
                <Space style={{ width: "100%" }}>
                  <Text>{displayFrom}</Text>
                  <Text>to</Text>

                  <Input
                    value={record[score.key]}
                    disabled={disabled}
                    style={{
                      width: "100%",
                      flex: 1,
                      border: hasError ? "1px solid red" : "",
                    }}
                    onKeyDown={(event) => {
                      const allowedKeys = [
                        "Backspace",
                        "Tab",
                        "ArrowLeft",
                        "ArrowRight",
                        "Delete",
                      ];

                      if (
                        !/[0-9]/.test(event.key) &&
                        !allowedKeys.includes(event.key)
                      ) {
                        event.preventDefault();
                        return;
                      }

                      const value = event.target.value + event.key;

                      if (Number(value) > 100) {
                        event.preventDefault();
                      }
                    }}
                    status={hasError ? "error" : ""}
                    onChange={(e) => {
                      // Allow free typing — validation happens on blur
                      handleSalaryDataChange(
                        record.key,
                        score.key,
                        e.target.value
                      );
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value === "" || value === null) return;

                      const numValue = Number(value);
                      const minAllowed =
                        fromValue !== null ? Number(fromValue) + 1 : 0;
                      if (fromValue !== null && numValue <= minAllowed) {
                        message.error(
                          `${score.label}: Value must be greater than ${minAllowed}`
                        );
                        handleSalaryDataChange(record.key, score.key, "");
                        return;
                      }

                      if (numValue > 100) {
                        message.error(`${score.label}: Maximum value is 100%`);
                        handleSalaryDataChange(record.key, score.key, "");
                        return;
                      }

                      handleSalaryDataChange(record.key, score.key, value);
                    }}
                  />

                  <Text>%</Text>
                </Space>
                {isRangeInvalid && (
                  <div style={{ color: "red", fontSize: "11px" }}>
                    Must be greater than {fromValue + 1}
                  </div>
                )}
              </Space>
            )}
          </>
        );
      },
    })),

    {
      title: "",
      width: 40,
      render: (_, record) => {
        const isLastRow = data[data.length - 1]?.key === record.key;
        return (
          isLastRow &&
          data.length > 1 && (
            <DeleteOutlined
              onClick={() => deleteRow(record.key)}
              style={{ color: "#1677ff", cursor: "pointer" }}
            />
          )
        );
      },
    },
  ].filter(Boolean);

  const handleSave = () => {
    console.log("All data values:", data);
  };

  const validateFirstTable = () => {
    let isValid = true;
    const invalidFirstTableRows = [];

    tableData.forEach((row, index) => {
      if (!row.salaryto || !row.label || !row.description) {
        invalidFirstTableRows.push(index);
        isValid = false;
      }

      // Also validate salaryto > salaryfrom
      if (
        row.salaryto !== "" &&
        row.salaryto !== null &&
        Number(row.salaryto) <= Number(row.salaryfrom)
      ) {
        if (!invalidFirstTableRows.includes(index)) {
          invalidFirstTableRows.push(index);
        }
        isValid = false;
      }
    });

    setInvalidFields((prev) => ({
      ...prev,
      strategic: invalidFirstTableRows,
    }));

    if (!isValid) {
      message.error(
        "Please fill in all required fields in Score Classification table"
      );
    }

    return isValid;
  };

  const validateSecondTable = () => {
    let isValid = true;
    const invalidSecondTableRows = [];

    data.forEach((row, index) => {
      const rowInvalid = {
        index: index,
        key: row.key,
        fields: [],
      };

      if (!row.experience) rowInvalid.fields.push("experience");

      // Validate each score: required + to > from
      scoreConfig.forEach((score, scoreIndex) => {
        const val = row[score.key];
        const fromVal = getPreviousScoreTo(row.key, scoreIndex);
        const isDisabled = isScoreDisabled(row.key, scoreIndex);

        // Skip disabled columns
        if (isDisabled) return;

        if (val === "" || val === null || val === undefined) {
          rowInvalid.fields.push(score.key);
        } else if (fromVal !== null && Number(val) <= Number(fromVal) + 1) {
          rowInvalid.fields.push(score.key);
        }
      });

      if (rowInvalid.fields.length > 0) {
        invalidSecondTableRows.push(rowInvalid);
        isValid = false;
      }
    });

    setInvalidSalaryFields(invalidSecondTableRows);

    if (!isValid) {
      message.error(
        "Please ensure all Score values are filled and each 'To' value is greater than its 'From' value in the Salary Increment Percentage table"
      );
    }

    return isValid;
  };

  const validateTables = () => {
    let isValid = true;

    if (!validateFirstTable()) {
      isValid = false;
    }

    if (Number(tableData?.[tableData?.length - 1]?.salaryto) === 100) {
      if (!validateSecondTable()) {
        isValid = false;
      }
    }

    return isValid;
  };

  const footerStyle = {
    position: "sticky",
    bottom: -20,
    left: 0,
    right: 0,
    backgroundColor: "#fff",
    padding: "10px",
    display: "flex",
    justifyContent: "end",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
    margin: "0px -20px",
  };

  return (
    <ScoreAndIncrementWrap>
      <h6 className="mb-3">Performance Score & Salary Increment percentage</h6>
      <h6>Score Classification</h6>
      <p className="lite-text">
        This table maps the final score from the Main Questionnaire Section to
        predefined performance categories. Based on the appraiser's evaluation,
        the appraisee is automatically placed into the appropriate score range,
        helping HR and managers clearly identify performance level and ensure
        consistent, objective appraisal outcomes across roles.
      </p>
      <StyledTable
        dataSource={tableData}
        columns={columns}
        className="small_table mb-3"
        rowKey="key"
        pagination={false}
      />
      {Number(tableData?.[tableData?.length - 1]?.salaryto) !== 100 &&
        status !== "view" && (
          <div className="footer-content" onClick={handleAddRow}>
            + Add
          </div>
        )}
      <br />
      <h6>Salary increment percentage</h6>
      <p className="lite-text">
        This table defines the percentage of salary increase applicable to each
        performance score range. Based on the appraiser's final evaluation score
        from the Main Questionnaire Section, the system highlights the eligible
        increment bracket, ensuring fair, transparent, and performance-driven
        compensation decisions.
      </p>
      {Number(tableData?.[tableData?.length - 1]?.salaryto) !== 100 ? (
        <>
          <div className="no-data">
            Complete score classification table till it meets 100% to start with
            salary increment percentage
          </div>
        </>
      ) : (
        <>
          <StyledTable
            columns={salaryColumns}
            dataSource={data}
            className="small_table mb-3"
            pagination={false}
          />
          {status !== "view" &&
            (() => {
              const lastRow = data[data.length - 1];
              // Disable + Add if any score column in the last row has reached 100
              const isMaxReached = scoreConfig.some(
                (score) => Number(lastRow?.[score.key]) >= 100
              );
              return (
                <div
                  className="footer-content"
                  onClick={!isMaxReached ? addRow : undefined}
                  style={{
                    cursor: isMaxReached ? "not-allowed" : "pointer",
                    opacity: isMaxReached ? 0.4 : 1,
                    pointerEvents: isMaxReached ? "none" : "auto",
                  }}
                >
                  + Add
                </div>
              );
            })()}
        </>
      )}
      {status !== "view" && (
        <div style={footerStyle}>
          <ButtonOutlined
            className="border-0"
            onClick={() => {
              setStatus("");
              setIsAddEditTemplate(false);
            }}
          >
            Cancel
          </ButtonOutlined>
          {status !== "edit" && (
            <>
              <ButtonOutlined
                loading={draftLoading}
                onClick={() => onSubmitClick("draft")}
              >
                Save as Draft
              </ButtonOutlined>
              <ButtonOutlined
                onClick={() => {
                  scrollToTop();
                  setStep(step - 1);
                }}
              >
                Previous
              </ButtonOutlined>
            </>
          )}
          <ButtonFilled
            type="primary"
            htmlType="submit"
            loading={loading}
            onClick={() => {
              if (validateTables()) {
                if (!validateAll()) return;
                onSubmitClick();
              }
            }}
          >
            {status === "edit" ? "Update" : "Complete"}
          </ButtonFilled>
        </div>
      )}
    </ScoreAndIncrementWrap>
  );
};

export default ScoreAndIncrement;
